import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store';
import { api } from '../utils/api';
import { ACCOUNT_COLORS } from '../utils/accountColors';
import type { NewMailNotice } from '../types';

// ── New-mail pop-up (Calem, report 2e5c8ac7) ─────────────────────────────────────────────────────────
// "pop-up appears on every SLF System page (inbox only) email in the user's default (starred) account
// showing sender + subject, and clicking it opens that email + page the user can turn it on or off."
// Mounted once in AuthGate (App.tsx), so it rides along on every staff page. Each new email in the Inbox
// of the starred account gets a card at the bottom right — sender, subject and a close × — stacked just
// above the corner's round "+" and Joy, newest at the bottom. A card fades after CARD_MS, not while the
// mouse is on it. Clicking it opens the email in PandaAI Email, in the tab that already shows Email (Calem,
// report b6182c13): this one, or the one the browser brings to the front — a My Dashboard(s) tab showing Email
// among them, when it shows the mailbox the email came into (Calem, report 5649663d); a new tab only when no
// open Email view can show it (MailApp reads ?open=). What counts as new, and the user's Email notifications
// switch (the bell over the ACCOUNTS list), are the server's call: GET /api/email/notify.
// A card wears the colour of the mailbox the email came into — the colour of its round badge in the Email
// sidebar, teal when it has none — and names that mailbox beside "New email", so it shows which account
// the email arrived in (Calem, report 56582ada). It used to wear the top menu's fixed Email/Calendar purple.
//
// Only the tab in front asks — once a minute, and again the moment it comes to the front. The browser's
// tabs share one marker and one list of the emails already shown (localStorage), so with ten SLF tabs
// open a new email pops up once, in the tab being looked at, and a tab opened meanwhile carries on from
// the others' marker rather than skipping what came in just before it. When no tab has asked for
// STALE_MS, the first ask starts afresh instead: the morning's first page doesn't pop up the night's mail.

// The tab showing PandaAI Email — /m/email, or the Dashboard on its Email tab — answers to EMAIL_TAB while it does
// (MailAppView), and opens the email in an EMAIL_OPEN_MSG sent to it. A My Dashboard(s) focus tab whose Email shows
// only some of the mailboxes answers to EMAIL_DASH_TAB instead (mailboxFilter, report 0b314fb0) and answers the same
// message, saying in an EMAIL_ACK_MSG whether it has the email open — so an email in a mailbox that dashboard leaves
// out still opens in a new Email tab (report 5649663d).
export const EMAIL_TAB = 'slf-email';
export const EMAIL_DASH_TAB = 'slf-email-dash';
export const EMAIL_OPEN_MSG = 'slf-email-open';
export const EMAIL_ACK_MSG = 'slf-email-ack';
type EmailOpenMsg = { type: typeof EMAIL_OPEN_MSG; open: string; account: string | null };
export type EmailAckMsg = { type: typeof EMAIL_ACK_MSG; open: string; shown: boolean };

// Whether this tab's Email view can show a mailbox: the full view (/m/email, the Dashboard's Email tab) shows every
// one, a focus dashboard's only the mailboxes that dashboard was given. An email with no mailbox of its own is one
// no filtered view can promise to show.
export function emailViewShows(accountId: string | null): boolean {
  const filter = useStore.getState().mailboxFilter;
  return !filter || (!!accountId && filter.includes(accountId));
}

// about:blank = nothing answered to that name, so the probe opened a tab of its own. A tab that has moved to another
// site cannot be read — and is not one of ours to send an email to either way.
function isBlank(w: Window | null): boolean {
  try { return w?.location.href === 'about:blank'; } catch { return false; }
}

const POLL_MS = 60_000;
const ACK_MS = 2_000;            // a tab brought to the front answers at once; no answer = it is gone
const CARD_MS = 30_000;          // how long a card stays
const AFTER_HOVER_MS = 4_000;    // once the mouse leaves it
const FADE_MS = 400;
const STALE_MS = 15 * 60_000;
const MAX_CARDS = 5;
const SEEN_KEEP = 100;

type Card = { id: string; accountId: string | null; account: NewMailNotice['account']; from: string; fromEmail: string; subject: string };
type Shared = { since: number; at: number; seen: string[] };   // at = when a tab last asked (this browser's clock)
const sharedKey = (userId: string) => `mf_newMail:${userId}`;
function readShared(userId: string): Shared | null {
  try {
    const v = JSON.parse(localStorage.getItem(sharedKey(userId)) || 'null');
    return v && Number.isFinite(v.since) && Number.isFinite(v.at) && Array.isArray(v.seen) ? v : null;
  } catch { return null; }
}
function writeShared(userId: string, v: Shared): void {
  try { localStorage.setItem(sharedKey(userId), JSON.stringify(v)); } catch { /* storage full — this tab still works */ }
}

export default function NewMailToast() {
  const me = useStore(s => s.me);
  const userId = me?.userId;
  // The endpoint sits behind the email module; asking without it would be refused, and a refusal pages
  // the admins.
  const canEmail = !!me?.modules?.includes('email');
  const [cards, setCards] = useState<Card[]>([]);
  const busy = useRef(false);

  const poll = useCallback(async () => {
    if (!userId || !canEmail || busy.current || document.visibilityState !== 'visible' || !localStorage.getItem('mf_token')) return;
    busy.current = true;
    try {
      const prev = readShared(userId);
      const carryOn = !!prev && Date.now() - prev.at < STALE_MS;
      const res = await api.getNewMail(carryOn ? prev!.since : undefined);
      // Read again: another tab may have shown some of these while this one was asking.
      const seen = carryOn ? (readShared(userId)?.seen || prev!.seen) : [];
      const fresh = res.on ? res.emails.filter(e => !seen.includes(e.id)) : [];
      writeShared(userId, { since: res.since, at: Date.now(), seen: [...seen, ...fresh.map(e => e.id)].slice(-SEEN_KEEP) });
      if (fresh.length) {
        // Latest first from the server; the stack runs oldest at the top to newest at the bottom.
        const add: Card[] = fresh.slice().reverse().map(e => ({
          id: e.id, accountId: res.accountId, account: res.account || null, from: e.from_name || e.from_email, fromEmail: e.from_email, subject: e.subject,
        }));
        setCards(cs => [...cs, ...add.filter(a => !cs.some(c => c.id === a.id))].slice(-MAX_CARDS));
      }
    } catch { /* transient — the next minute asks again */ }
    finally { busy.current = false; }
  }, [userId, canEmail]);

  useEffect(() => {
    if (!userId || !canEmail) return;
    void poll();
    const timer = window.setInterval(() => { void poll(); }, POLL_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [userId, canEmail, poll]);

  const drop = (id: string) => setCards(cs => cs.filter(c => c.id !== id));
  const open = (c: Card) => {
    drop(c.id);
    const msg: EmailOpenMsg = { type: EMAIL_OPEN_MSG, open: c.id, account: c.accountId };
    const origin = window.location.origin;
    const acct = c.accountId ? `&account=${encodeURIComponent(c.accountId)}` : '';
    const href = `/m/email?open=${encodeURIComponent(c.id)}${acct}`;
    // This tab already shows Email and shows this mailbox — the Email tab, or a dashboard of the user's own
    // (report 5649663d): it opens the email where the user is, with no new tab at all.
    if ((window.name === EMAIL_TAB || window.name === EMAIL_DASH_TAB) && emailViewShows(c.accountId)) {
      window.postMessage(msg, origin);
      return;
    }
    // Brings the Email tab to the front, or opens a blank tab when there is none. Only that tab is sent the
    // email; another Email view left open elsewhere keeps what it shows. A name this tab answers to itself is
    // never probed — it was asked above, and a probe would only hand this tab back to itself.
    const w = window.name === EMAIL_TAB ? null : window.open('', EMAIL_TAB);
    if (w && !isBlank(w)) { w.postMessage(msg, origin); return; }
    // The blank tab is where the email opens, unless a dashboard takes it below. It is unnamed first, so a page
    // that never gets to Email (a login redirect) doesn't keep the name; the Email page names it once showing.
    const fresh = () => {
      if (w) { w.name = ''; w.location.href = href; }
      else window.open(href, '_blank');   // no blank tab to use — ask for one outright
    };
    // No Email tab. A My Dashboard(s) tab showing Email comes to the front instead and is asked for the email:
    // it opens it when it shows that mailbox and hands it back when it doesn't (EMAIL_ACK_MSG). A probe that
    // finds nothing leaves a blank tab of its own to close.
    const d = window.name === EMAIL_DASH_TAB ? null : window.open('', EMAIL_DASH_TAB);
    if (!d || isBlank(d)) {
      if (d) { try { d.close(); } catch { /* the probe's own tab — nothing of the user's is closed */ } }
      fresh();
      return;
    }
    let timer = 0;
    const onAck = (e: MessageEvent) => {
      const a = e.data;
      if (e.origin !== origin || a?.type !== EMAIL_ACK_MSG || a.open !== c.id) return;
      window.removeEventListener('message', onAck);
      window.clearTimeout(timer);
      // It has the email open, so the blank tab is not needed; or that mailbox is one it leaves out
      // (report 0b314fb0), and the email opens in an Email tab of its own after all.
      if (a.shown) { try { w?.close(); } catch { /* opened by this click — never a tab of the user's */ } }
      else fresh();
    };
    window.addEventListener('message', onAck);
    timer = window.setTimeout(() => { window.removeEventListener('message', onAck); fresh(); }, ACK_MS);
    d.postMessage(msg, origin);
  };

  // Under the dialogs (5400 and up) and Joy (9000); over the page, its "+" (100), the toast line (2000)
  // and the upload card (4000). The empty stack lets clicks through.
  return (
    <div aria-live="polite" style={{ position: 'fixed', right: 24, bottom: 92, zIndex: 4500, width: 320, maxWidth: 'calc(100vw - 48px)', display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
      {cards.map(c => <MailCard key={c.id} card={c} onOpen={() => open(c)} onClose={() => drop(c.id)} />)}
    </div>
  );
}

function MailCard({ card, onOpen, onClose }: { card: Card; onOpen: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const fadeAfter = (ms: number) => { window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setLeaving(true), ms); };
  useEffect(() => { fadeAfter(CARD_MS); return () => window.clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Gone once the fade has run; the mouse coming back over it in time brings it back.
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(onClose, FADE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]); // eslint-disable-line react-hooks/exhaustive-deps
  // The mailbox's own colour and name (the sidebar's badge and first line); its address on hover.
  const c = ACCOUNT_COLORS[card.account?.color || ''] || ACCOUNT_COLORS.teal;
  const mailbox = card.account?.name || card.account?.email || '';

  return (
    <div role="status"
      onMouseEnter={() => { window.clearTimeout(timer.current); setLeaving(false); }}
      onMouseLeave={() => fadeAfter(AFTER_HOVER_MS)}
      style={{ pointerEvents: 'auto', display: 'flex', alignItems: 'flex-start', gap: 6, padding: '10px 8px 10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${c.accent}`, borderRadius: 12, boxShadow: 'var(--shadow-md)', animation: 'slideUp 0.25s ease', opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease` }}>
      <button onClick={onOpen} title={t('eml_notify_open')}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10, padding: 0, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
        <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.bg, color: c.accent }}>
          <i className="ti ti-mail" style={{ fontSize: 16 }} aria-hidden />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: c.accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t('eml_notify_new')}
            {mailbox && <span title={card.account?.email}>{` · ${mailbox}`}</span>}
          </span>
          <span title={card.fromEmail} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{card.from}</span>
          <span style={{ fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{card.subject}</span>
        </span>
      </button>
      <button onClick={onClose} title={t('eml_notify_close')} aria-label={t('eml_notify_close')}
        style={{ flexShrink: 0, width: 22, height: 22, padding: 0, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
      </button>
    </div>
  );
}
